<script setup>
import axios from 'axios';
import { ref, onMounted } from "vue";
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Toast from 'primevue/toast';
import Dialog from 'primevue/dialog';
import { FilterMatchMode } from '@primevue/core/api';
import { useToast } from "primevue/usetoast";

const users = ref();
const toast = useToast();
const showPassword = ref(false);
const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const selectedUser = ref(null);

const changePasswordDialog = ref(false);
const deleteUserDialog = ref(false);
const newUserDialog = ref(false);
const newPassword = ref('');
const username = ref('');
const documento = ref('');

const showChangePasswordDialog = (user) => {
  selectedUser.value = user;
  changePasswordDialog.value = true;
  newPassword.value = '';
};

const showDeleteUserDialog = (user) => {
  selectedUser.value = user;
  deleteUserDialog.value = true;
};

const showNewUserDialog = () => {
  newUserDialog.value = true;
  username.value = '';
  documento.value = '';
};

async function deleteUser() {
  try {
    let response = await axios.delete('https://cuadrecaja.fenix-pos.com/api/delete-user', 
    // let response = await axios.delete('http://localhost/backend_cuadre_caja_pos/api/delete-user', 
    { data: { username: selectedUser.value.username } });

    if (response.status === 200) {
      toast.add({
        severity: 'success',
        summary: 'Usuario eliminado',
        detail: 'El usuario se eliminó correctamente.',
        life: 3000
      });
      getUsers();
      deleteUserDialog.value = false;
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'No se pudo eliminar el usuario',
      detail: 'Hubo un error al eliminar el usuario. Por favor, inténtelo de nuevo.',
      life: 3000
    });
  }
};

// Función para cambiar la contraseña
async function changePassword() {
    try {
        let response = await axios.post('https://cuadrecaja.fenix-pos.com/api/change-password', 
            { username: selectedUser.value.username, password: newPassword.value }
        );

        if (response.status === 200) {
            toast.add({
                severity: 'success',
                summary: 'Contraseña cambiada',
                detail: 'La contraseña se cambió correctamente.',
                life: 3000
            });
            changePasswordDialog.value = false;
        }
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Contraseña no se pudo cambiar',
            detail: 'Hubo un error al cambiar la contraseña. Por favor, inténtelo de nuevo.',
            life: 3000
        });
    }
};

async function saveUser() {
    try {
        // let response = await axios.post('http://localhost/backend_cuadre_caja_pos/api/save-user', 
        let response = await axios.post('https://cuadrecaja.fenix-pos.com/api/save-user', 
            { name: username.value.toUpperCase(), username: documento.value, password: documento.value }
        );

        if (response.status === 200) {
            toast.add({
                severity: 'success',
                summary: 'Usuario Creado',
                detail: 'El usuario se creó correctamente.',
                life: 3000
            });
            newUserDialog.value = false;
            getUsers();
        }
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'No se pudo crear el usuario',
            detail: 'Hubo un error al crear el usuario. Por favor, inténtelo de nuevo.',
            life: 3000
        });
    }
};

async function getUsers() {
    try {
        let response = await axios.get('https://cuadrecaja.fenix-pos.com/api/get-users');
        
        users.value = response.data;
    } catch (error) {
        console.log(error);
    }
}

// Cargar usuarios
onMounted(() => {
  getUsers();
});
</script>

<template>
  <div class="py-4 container-fluid">
    <Toast />
    <div class="card z-index-2">
      <div class="row p-4 justify-div">
        <div class="col-md-3">
          <IconField>
            <InputIcon><i class="pi pi-search" /></InputIcon>
            <InputText 
                class="input" 
                v-model="filters.global.value" 
                type="text"
                autocomplete="off"
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
                placeholder="Buscar usuario..." 
            />
          </IconField>    
        </div>
        <div class="col-md-2">
          <button class="button-save" @click="showNewUserDialog"><i class="pi pi-user-plus margin-icon" />Agregar Usuario</button>
        </div>
      </div>
      <div class="col-lg-12 mb-lg-0 mb-4">
        <div class="card">
          <div class="mt-3 px-2">
            <DataTable 
              class="m-3" 
              :value="users" 
              :paginator="true" 
              :rows="6"
              :rowsPerPageOptions="[6, 10, 20, 50]" 
              :filters="filters"
              stripedRows 
              resizableColumns 
              columnResizeMode="fit" 
              showGridlines 
              tableStyle="min-width: 50rem"
            >
            <Column field="name" header="Nombre" sortable style="min-width: 8rem"></Column>
              <Column field="username" header="Usuario" sortable style="min-width: 8rem"></Column>
              
              <Column header="Opciones" style="max-width: 20rem">
                <template #body="slotProps">
                  <div class="buttons-options">
                    <Button 
                      icon="pi pi-key" 
                      outlined 
                      rounded 
                      severity="help" 
                      @click="showChangePasswordDialog(slotProps.data)"
                      label="Cambiar contraseña"
                    />
                    <Button 
                      icon="pi pi-trash" 
                      outlined 
                      rounded 
                      severity="danger" 
                      @click="showDeleteUserDialog(slotProps.data)"
                      label="Eliminar usuario"
                    />
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </div>
    </div>

    <!-- Diálogo para cambiar contraseña -->
    <Dialog 
      v-model:visible="changePasswordDialog" 
      modal 
      header="Cambiar contraseña" 
      :style="{ width: '25rem' }"
    >
      <div class="flex align-items-center gap-3 mb-3">
        <div class="flex flex-column" style="width: 100%">
            <label for="newPassword">Nueva contraseña para: {{ selectedUser?.name }}</label>
            <InputText 
            id="newPassword" 
            v-model="newPassword" 
            :type="showPassword ? 'text' : 'password'"
            autocomplete="new-password"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            placeholder="Ingrese nueva contraseña"
            />
            <InputIcon @click="showPassword = !showPassword" class="input-icon">
                <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'" />
            </InputIcon>
        </div>
      </div>
      <div class="buttons">
        <Button 
          type="button" 
          label="Cancelar" 
          severity="secondary" 
          @click="changePasswordDialog = false"
        ></Button>
        <Button 
          type="button" 
          label="Cambiar" 
          @click="changePassword"
          :disabled="!newPassword"
        ></Button>
      </div>
    </Dialog>

    <!-- Diálogo para agregar un nuevo usuario -->
    <Dialog 
      v-model:visible="newUserDialog" 
      modal 
      header="Agregar Usuario" 
      :style="{ width: '30rem', heigth: '20rem' }"
    >
      <div class="flex align-items-center gap-3 mb-3">
        <div class="flex flex-column dialogUser" style="width: 100%">
            <InputText 
            id="username" 
            v-model="username" 
            type="text"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            placeholder="Nombre de usuario"
            />
            <InputText 
            id="documento" 
            v-model="documento" 
            type="text"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            placeholder="Número de documento"
            />
        </div>
      </div>
      <div class="buttons">
        <Button 
          type="button" 
          label="Cancelar" 
          severity="secondary" 
          @click="newUserDialog = false"
        ></Button>
        <Button 
          type="button" 
          label="Guardar" 
          @click="saveUser"
          :disabled="!username || !documento"
        ></Button>
      </div>
    </Dialog>

    <!-- Diálogo confirmación de borrado -->
    <Dialog 
      v-model:visible="deleteUserDialog"
      modal 
      header="Agregar Usuario" 
      :style="{ width: '30rem', heigth: '20rem' }"
    >
      <div class="flex align-items-center gap-3 mb-3">
        <div class="flex flex-column dialogUser" style="width: 100%">
            <h2>¿Está seguro de borrar el usuario?</h2>
            <p>Nombre: {{ selectedUser?.name }}<br>Documento: {{ selectedUser?.username }}</p>
            <p>El usuario se borrará de forma permanente.</p>
        </div>
      </div>
      <div class="buttons">
        <Button 
          type="button" 
          label="Cancelar" 
          severity="secondary" 
          @click="deleteUserDialog = false"
        ></Button>
        <Button 
          type="button" 
          label="Eliminar"
          class="button-danger"
          @click="deleteUser"
        ></Button>
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
.input {
    height: 3rem !important;
    width: 100%;
}

.button-margin {
    margin-right: 8px;
}

.buttons {
    display: flex;
    flex-direction: row;
    gap: 20px;
    justify-content: flex-end;
}

.input-icon {
    cursor: pointer;
    margin-left: 10px;
    position: relative;
}

.justify-div {
    justify-content: space-between;
    align-items: center;
}

.button-save {
    background-color: #11cdef;
    color: white;
    border-radius: 15px;
    padding: 12px 0px;
    cursor: pointer;
    font-size: 18px;
    font-weight: bold;
    border: none;
    width: 240px;
}

.margin-icon {
    margin-right: 10px;
}

.dialogUser {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.buttons-options {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}

.button-danger {
    background-color: #ff0000;
    color: white;
    border: none;
}

.p-button:not(:disabled):hover {
    background-color: #bb0000;
    color: white;
    border: none;
}

@media (max-width: 1444px) {
  .button-save {
    background-color: #11cdef;
    color: white;
    border-radius: 15px;
    padding: 12px 0px;
    cursor: pointer;
    font-size: 18px;
    font-weight: bold;
    border: none;
    width: 230px;
    margin-left: -100px;
  }
}
</style>